import { CustomFormattedMessage } from '../../../i18n/i18n';
import Select from '@rio-cloud/rio-uikit/lib/es/Select';
import React from 'react';
import {
  getFromFilter,
  getPeriodFilter,
  getToFilter,
  setFrom,
  setPeriod,
  setServiceTypes,
  setTo,
  useDashboardFilteredTransactions,
} from '../../../state/transactions/transactionFilterSlice';
import { useAppDispatch, useAppSelector } from '../../../state/hooks';
import TableToolbar from '@rio-cloud/rio-uikit/lib/es/TableToolbar';
import Button from '@rio-cloud/rio-uikit/lib/es/Button';
import Multiselect from '@rio-cloud/rio-uikit/lib/es/Multiselect';
import { useTransactionUpdateState } from '../../../state/transactions/transactionUpdateSlice';
import {
  usePeriodSelectOptions,
  parsePeriod,
} from '../../../usePeriodSelectOptions';
import moment from 'moment';
import { ServiceType } from '../../../codegen/transactions';
import { DateFilterPicker } from '../../../components/DateFilterPicker';

export const FilterBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { serviceTypeOptions } = useDashboardFilteredTransactions();
  const from = moment(useAppSelector(getFromFilter));
  const to = moment(useAppSelector(getToFilter));
  const period = useAppSelector(getPeriodFilter);
  const { transactionFilterPeriods } = usePeriodSelectOptions();

  const { hasTransactionUpdate, reset: resetTransactionUpdateState } =
    useTransactionUpdateState();

  const { resetApiState } = useDashboardFilteredTransactions();

  const handleRefreshTransactions = () => {
    resetApiState();
    resetTransactionUpdateState();
  };

  return (
    <TableToolbar>
      <div className="table-toolbar-container ">
        <div className="table-toolbar-group-left justify-content-center">
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar">
              <DateFilterPicker
                labelId={'common.startDate'}
                value={from}
                onChange={(d) => dispatch(setFrom(d))}
                isDateBlocked={(d) => d.isAfter(to, 'day')}
                errorMessageId={(d) =>
                  d.isAfter(to, 'day') && 'common.timerange.beforeEndDate'
                }
                data-testid={'dashboard-filter-bar-start-date-picker'}
              />
            </div>
          </div>
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar">
              <DateFilterPicker
                labelId={'common.endDate'}
                value={to}
                onChange={(d) => dispatch(setTo(d))}
                isDateBlocked={(d) => d.isBefore(from, 'day')}
                errorMessageId={(d) =>
                  (d.isBefore(from, 'day') &&
                    'common.timerange.afterStartDate') ||
                  (d.isAfter(moment(), 'day') &&
                    'common.timerange.beforeFuture')
                }
                data-testid={'dashboard-filter-bar-end-date-picker'}
              />
            </div>
          </div>
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar">
              <div data-testid={'dashboard-filter-bar-period-selector'}>
                <label>
                  <CustomFormattedMessage id={'common.period'} />
                </label>
                <Select
                  useClear
                  className={'width-200'}
                  id={'periodSelector'}
                  placeholder={
                    <CustomFormattedMessage id={'common.selectPeriod'} />
                  }
                  options={transactionFilterPeriods}
                  onChange={(option) => {
                    const value = option ? parsePeriod(option.id) : undefined;
                    dispatch(setPeriod(value));
                  }}
                  value={period ? [period.toString()] : []}
                />
              </div>
            </div>
          </div>
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar gap-5">
              <div data-testid={'dashboard-filter-bar-service-types-selector'}>
                <label className="min-width-300">
                  <CustomFormattedMessage
                    id={'dashboard.filter.serviceTypes.header'}
                  />
                </label>
                <Multiselect
                  placeholder={
                    <CustomFormattedMessage
                      id={'dashboard.filter.serviceTypes.placeholder'}
                    />
                  }
                  options={serviceTypeOptions}
                  onChange={(options: string[]) =>
                    dispatch(setServiceTypes(options as ServiceType[]))
                  }
                />
              </div>
            </div>
          </div>
          {hasTransactionUpdate && (
            <div className="table-toolbar-column">
              <div className="btn-toolbar table-btn-toolbar">
                <Button
                  data-testid="dashboard-filter-bar-refresh-button"
                  bsStyle={'primary'}
                  onClick={handleRefreshTransactions}
                >
                  <span className={'rioglyph rioglyph-refresh'} />
                  <CustomFormattedMessage
                    id={'transactions.button.refreshTransactions'}
                  />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </TableToolbar>
  );
};
